'use client';

import { clients } from '@bb/lib/clients';

import {
  SlackChannel,
  getMailingListSubscriberMessage,
} from '@baseball-near-you/common';
import { Button, Form, FormInput, toast } from '@baseball-near-you/ui';
import { useMutation } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

export function useEmailListSubscriptionMutation() {
  const pathname = usePathname();
  const {
    mutateAsync: handleSubscribeToEmailList,
    isLoading: isSubscribingToEmailList,
  } = useMutation({
    mutationFn: async (email: string) => {
      await clients.klaviyo.createProfile({ payload: { email } });
    },
    onSuccess: async (profile, email) => {
      await clients.slack.postMessage({
        payload: {
          channel: SlackChannel.MailingList,
          text: 'New mailing list subscriber',
          ...getMailingListSubscriberMessage(email, pathname),
        },
      });
      toast.success('Thank you for subscribing!', {
        description:
          "It's the best way to stay up to date with Baseball Near You!",
      });
    },
    onError: async () => {
      await toast.error(
        'Failed to subscribe to the mailing list. Please try again later.'
      );
    },
  });

  return {
    handleSubscribeToEmailList,
    isSubscribingToEmailList,
  };
}

export function EmailSubscriptionForm() {
  const { handleSubscribeToEmailList } = useEmailListSubscriptionMutation();

  return (
    <Form
      onSubmit={async ({ email }) => {
        await handleSubscribeToEmailList(email);
      }}
      schema={z.object({
        email: z.string().email().min(1, 'Required'),
      })}
      className="space-y-3"
    >
      <FormInput
        name="email"
        label=""
        className="border-white/50 bg-white/25 text-white placeholder:text-white/75"
        placeholder="Enter your email..."
      />
      <EmailSubscriptionButton />
    </Form>
  );
}

export const EmailSubscriptionButton = () => {
  const { formState } = useFormContext();
  return (
    <Button
      className="w-full text-red"
      type="submit"
      variant={'secondary'}
      loading={formState.isSubmitting}
    >
      Submit
    </Button>
  );
};
